// ============================================
// COMMAND HANDLERS
// ============================================

import {
    GameStartedEvent,
    PokPlacedEvent,
    PokMovedEvent,
    PokRemovedEvent,
    RoundEndedEvent,
    RoundStartedEvent,
    TableFlippedEvent,
    GameResetEvent
} from './events.js';
import { StorageService } from './utils/storage-service.js';
import { PLAYERS } from './config.js';
import { ScoringService } from './scoring-service.js';

export class CommandHandler {
    constructor(eventStore, gameStateProjection) {
        this.eventStore = eventStore;
        this.gameState = gameStateProjection;
    }

    getNextPokId() {
        // Calculate next POK ID by finding the highest existing ID in events
        const pokPlacedEvents = this.eventStore.getEventsByType('POK_PLACED');

        let maxId = -1;
        pokPlacedEvents.forEach(event => {
            const match = event.data.pokId.match(/^pok-(\d+)$/);
            if (match) {
                const pokNumber = parseInt(match[1], 10);
                if (pokNumber > maxId) {
                    maxId = pokNumber;
                }
            }
        });

        return `pok-${maxId + 1}`;
    }

    startGame(startingPlayerId, redName = PLAYERS.RED, blueName = PLAYERS.BLUE) {
        const state = this.gameState.getState();
        if (state.isStarted) {
            throw new Error('Game already started');
        }

        this.eventStore.append(new GameStartedEvent(startingPlayerId, redName, blueName));
    }

    placePok(playerId, x, y) {
        const round = this.gameState.getCurrentRound();
        if (!round) {
            throw new Error('No active round');
        }

        if (this.gameState.isRoundComplete(round)) {
            throw new Error('Round is complete');
        }

        // Check if player has POKs remaining
        const poksRemaining = this.gameState.getPoksRemaining(round, playerId);

        if (poksRemaining === 0) {
            throw new Error('No POKs remaining for player');
        }

        // Check if it's the correct player's turn
        const nextPlayer = this.gameState.getNextPlayer();
        if (playerId !== nextPlayer) {
            throw new Error(`Not ${playerId}'s turn`);
        }

        const pokId = this.getNextPokId();
        this.eventStore.append(new PokPlacedEvent(pokId, playerId, x, y));
    }

    movePok(pokId, newX, newY) {
        const round = this.gameState.getCurrentRound();
        if (!round) return;

        const pok = round.poks.find(p => p.id === pokId);
        if (!pok) return;

        this.eventStore.append(new PokMovedEvent(pokId, newX, newY));
    }

    removePok(pokId) {
        const round = this.gameState.getCurrentRound();
        if (!round) return;

        // Only allow removing last placed POK
        if (pokId !== round.lastPlacedPokId) {
            throw new Error('Can only remove last placed POK');
        }

        this.eventStore.append(new PokRemovedEvent(pokId));
    }

    endRound() {
        const round = this.gameState.getCurrentRound();
        if (!round) return;

        if (!this.gameState.isRoundComplete(round)) {
            throw new Error('Round not complete');
        }

        // Check if ROUND_ENDED has already been fired for this round
        const roundEndedEvents = this.eventStore.getEventsByType('ROUND_ENDED');
        const alreadyEnded = roundEndedEvents.some(e => e.data.roundNumber === round.roundNumber);
        if (alreadyEnded) {
            return; // Round already ended, don't fire again
        }

        // Event only stores roundNumber - scores will be recalculated from poks
        this.eventStore.append(new RoundEndedEvent(round.roundNumber));
    }

    startNextRound() {
        const state = this.gameState.getState();
        const prevRound = this.gameState.getCurrentRound();

        if (!prevRound || !this.gameState.isRoundComplete(prevRound)) {
            throw new Error('Cannot start next round');
        }

        // Determine starter: winner of previous round, or alternate if tie
        const winner = ScoringService.getRoundWinner(prevRound);
        let starter;
        if (winner) {
            starter = winner;
        } else {
            // Tie: alternate
            starter = prevRound.startingPlayerId === PLAYERS.RED ? PLAYERS.BLUE : PLAYERS.RED;
        }

        // Start the next round first (inherits current flip state)
        const nextRoundNumber = state.rounds.length;
        this.eventStore.append(new RoundStartedEvent(nextRoundNumber, starter));

        // Then flip the table for the new round
        this.eventStore.append(new TableFlippedEvent(!state.isFlipped));
    }

    flipTable(isFlipped) {
        this.eventStore.append(new TableFlippedEvent(isFlipped));
    }

    resetGame() {
        // Clear all events and localStorage to completely reset the game
        this.eventStore.clear();
        StorageService.remove('pok-event-store');

        // Manually trigger a special reset event that won't be stored
        // This allows projections to reset their state properly
        this.eventStore.publish(new GameResetEvent());
    }

    /**
     * Undo the last round end, allowing the user to edit the board again
     * @returns {boolean} - Whether the undo was successful
     */
    undoRoundEnd() {
        return this.eventStore.removeLastEventOfType('ROUND_ENDED');
    }
}
